import type { AccessUser } from '@/utils/accessControl'
import type { TFunction } from 'i18next'

export type PlatformMenuItem = {
  key: string
  label: string
  icon: string
  url?: string
  children?: PlatformMenuItem[]
}

export const BUSINESS_MENU_SECTIONS = [
  {
    title: 'Main',
    items: [
      { key: 'dashboard', label: 'Dashboard', icon: 'tabler:smart-home', url: '/dashboard/sales' },
      { key: 'analytics', label: 'Analytics', icon: 'tabler:chart-bar', url: '/dashboard/analytics' },
      { key: 'sale', label: 'New Order', icon: 'tabler:clipboard-plus', url: '/apps/sale' },
      { key: 'wholesale', label: 'Accounts', icon: 'tabler:users', url: '/apps/wholesale' },
    ],
  },
  {
    title: 'Manage',
    items: [
      { key: 'inventory', label: 'Inventory', icon: 'tabler:packages', url: '/inventory/products' },
      { key: 'expenses', label: 'Expenses', icon: 'tabler:receipt-refund', url: '/dashboard/expenses/add' },
      { key: 'activitylogs', label: 'Activity logs', icon: 'tabler:history', url: '/dashboard/activitylogs' },
      {
        key: 'sample_management',
        label: 'Pending approvals',
        icon: 'tabler:clock-check',
        children: [
          { key: 'sampleholdings', label: 'Sample Intake', url: '/dashboard/samplesholdings' },
          { key: 'sampleviewing', label: 'Create Order', url: '/dashboard/sampleviewing' },
          { key: 'sampleviewingmanagement', label: 'Work Order', url: '/dashboard/sampleviewingworker' },
        ],
      },
    ],
  },
  {
    title: 'System',
    items: [
      { key: 'notifications', label: 'Notifications', icon: 'tabler:bell', url: '/dashboard/notifications' },
      { key: 'telegram', label: 'Telegram bot', icon: 'tabler:brand-telegram', url: '/dashboard/telegram' },
      {
        key: 'config',
        label: 'Config',
        icon: 'tabler:settings',
        children: [
          { key: 'users', label: 'Users', url: '/config/users' },
          { key: 'categories', label: 'Categories', url: '/config/categories' },
          { key: 'settings', label: 'General Settings', url: '/config/settings' },
        ],
      },
      {
        key: 'reports',
        label: 'Reports',
        icon: 'tabler:file-description',
        children: [
          { key: 'outofstock', label: 'Out of Stock', url: '/reports/outofstock' },
          { key: 'lowinventory', label: 'Low Inventory', url: '/reports/lowinventory' },
        ],
      },
    ],
  },
] as const

export const PLATFORM_MENU_SECTIONS = [
  {
    title: 'Platform',
    items: [
      {
        key: 'platform-tenants',
        label: 'Tenants & workers',
        icon: 'tabler:building-store',
        url: '/platform/tenants',
      },
      {
        key: 'platform-plans',
        label: 'Subscription plans',
        icon: 'tabler:credit-card',
        url: '/platform/plans',
      },
      {
        key: 'platform-subscriptions',
        label: 'Subscriptions',
        icon: 'tabler:receipt',
        url: '/platform/subscriptions',
      },
      {
        key: 'platform-integrations',
        label: 'Integrations',
        icon: 'tabler:plug-connected',
        url: '/platform/integrations',
      },
      {
        key: 'platform-settings',
        label: 'Platform settings',
        icon: 'tabler:adjustments',
        url: '/platform/settings',
      },
    ],
  },
  {
    title: 'Account',
    items: [
      { key: 'profile', label: 'Profile', icon: 'tabler:user', url: '/dashboard/profile' },
    ],
  },
] as const

type FlatMenuEntry = {
  key: string
  label: string
  url: string
}

function flattenMenuSections(
  sections: ReadonlyArray<{ items: ReadonlyArray<{ key: string; label: string; url?: string; children?: ReadonlyArray<{ key: string; label: string; url?: string }> }> }>
): FlatMenuEntry[] {
  const entries: FlatMenuEntry[] = []

  for (const section of sections) {
    for (const item of section.items) {
      if (item.url) {
        entries.push({ key: item.key, label: item.label, url: item.url })
      }
      if (item.children) {
        for (const child of item.children) {
          if (child.url) {
            entries.push({ key: child.key, label: child.label, url: child.url })
          }
        }
      }
    }
  }

  return entries
}

const ALL_MENU_ENTRIES: FlatMenuEntry[] = [
  ...flattenMenuSections(BUSINESS_MENU_SECTIONS),
  ...flattenMenuSections(PLATFORM_MENU_SECTIONS),
]

/** Resolve the sidebar menu item that best matches the current path. */
export function findMenuEntryForPath(pathname: string): FlatMenuEntry | null {
  const exact = ALL_MENU_ENTRIES.find((entry) => entry.url === pathname)
  if (exact) return exact

  const prefixMatches = ALL_MENU_ENTRIES.filter((entry) => pathname.startsWith(`${entry.url}/`))
  if (prefixMatches.length === 0) return null

  return prefixMatches.sort((a, b) => b.url.length - a.url.length)[0]
}

/** Page title using the same menu key + label as the sidebar. */
export function getMenuTitleFromPath(pathname: string, t?: TFunction): string | null {
  const entry = findMenuEntryForPath(pathname)
  if (!entry) return null
  return t ? t(`menu.${entry.key}`, { defaultValue: entry.label }) : entry.label
}

/** Paths a platform operator may open without impersonating a tenant. */
export const PLATFORM_OPERATOR_PATH_PREFIXES = ['/platform', '/dashboard/profile', '/auth/logout']

export function isPlatformOperatorPath(pathname: string): boolean {
  return PLATFORM_OPERATOR_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

export function getMenuSectionsForUser(user: AccessUser | null | undefined) {
  if (user?.role === 'superadmin' && !user?.impersonatedBy) {
    return PLATFORM_MENU_SECTIONS
  }
  return BUSINESS_MENU_SECTIONS
}

export const BUSINESS_MOBILE_NAV_KEYS = ['dashboard', 'sale', 'wholesale', 'inventory'] as const
export const PLATFORM_MOBILE_NAV_KEYS = [
  'platform-tenants',
  'platform-plans',
  'platform-subscriptions',
  'profile',
] as const

export type MobileNavItem = {
  key: string
  label: string
  icon: string
  url: string
}

export function isMobileNavItemActive(pathname: string, url: string) {
  if (pathname === url) return true
  return pathname.startsWith(`${url}/`)
}

export function getMobilePrimaryNavItems(
  sections: ReadonlyArray<{
    items: ReadonlyArray<{
      key: string
      label: string
      icon?: string
      url?: string
    }>
  }>,
  primaryKeys: readonly string[]
): MobileNavItem[] {
  const byKey = new Map<string, MobileNavItem>()

  for (const section of sections) {
    for (const item of section.items) {
      if (item.url && item.icon && primaryKeys.includes(item.key)) {
        byKey.set(item.key, {
          key: item.key,
          label: item.label,
          icon: item.icon,
          url: item.url,
        })
      }
    }
  }

  return primaryKeys
    .map((key) => byKey.get(key))
    .filter((item): item is MobileNavItem => Boolean(item))
}
